import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';

import { CodeTypeModel }                from '../core/models/code-type';
import { CompanyListsActionsComponent } from './company-lists.actions.component';
import { CompanyListsService }          from './company-lists.service';
import { CompanyService }               from '../companies/company.service';
import { ConfirmService }               from '../messages/confirm/confirm.service';
import { MessagesService }              from '../messages/messages.service';
import { PaymentMethodModel }           from './typings/payment-method';
import { RemoveComponent }              from '../table/cell-components/actions/remove.component';
import { Table }                        from '../table/types/table';
import { TableColumn }                  from '../table/types/table-column';
import { TableSelectControlComponent }  from '../table/cell-components/controls/select-control/table-select-control.component';
import { TableSwitchControlComponent }  from '../table/cell-components/controls/switch-control/table-switch-control.component';
import { TableTextControlComponent }    from '../table/cell-components/controls/text-control/table-text-control.component';

@Component({
  selector   : 'wor-company-lists-payment-methods',
  template: require('./company-lists.payment-methods.component.html')
})
export class CompanyListsPaymentMethodsComponent implements OnInit {
  @Input() paymentMethods: Array<PaymentMethodModel> = [];

  accounts       : Array<CodeTypeModel>            = [];
  originalMethods: Map<string, PaymentMethodModel> = new Map();
  tableSettings  : Table                           = {};

  collapsed       = false;
  loadingAccounts = false;

  constructor (
    private companyListsService: CompanyListsService,
    public  companyService     : CompanyService,
    private confirmService     : ConfirmService,
    private messagesService    : MessagesService,
    private translateService   : TranslateService
  ) {}

  ngOnInit () : void {
    if ( this.hasXeroAccountColumn() ) {
      this.loadingAccounts = true;

      this.companyService.getPaymentAccounts()
      .then(( accounts : Array<CodeTypeModel> ) => {
        this.accounts = accounts;
      })
      .finally(() => {
        this.loadingAccounts = false;
        this.tableSettings   = this.getTableSettings();

        this.refreshTable();
      });
    }
    else {
      this.tableSettings = this.getTableSettings();
    }
  }

  addPaymentMethod () : void {
    this.paymentMethods = [
      ...this.paymentMethods,
      {
        account_code  : '',
        payment_method: '',
        focusField    : 'payment_method',
        editing       : true,
        use_eftpos    : false
      }
    ];
  }

  clearFocusField ( paymentMethod : PaymentMethodModel, field : 'payment_method' ) : void {
    if ( paymentMethod?.focusField === field ) {
      paymentMethod.focusField = undefined;
    }
  }

  deletePaymentMethod ( paymentMethod : PaymentMethodModel ) : void {
    if ( !paymentMethod.id ) {
      this.paymentMethods = this.paymentMethods.filter(method => method !== paymentMethod);

      return;
    }

    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_TEMPLATE'))
    .then(() => this.companyListsService.deletePaymentMethod(paymentMethod))
    .then(() => {
      this.paymentMethods = this.paymentMethods.filter(method => method.id !== paymentMethod.id);

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.PAYMENT_METHOD'), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  getTableSettings () : Table {
    const columns : Array<TableColumn> = [
      {
        component: {
          bindings: {
            outputs: {
              onRemoveClick: ( paymentMethod : PaymentMethodModel ) => this.deletePaymentMethod(paymentMethod)
            }
          },
          enabled: true,
          factory: RemoveComponent
        },
        sortable: false
      },
      {
        component: {
          bindings: {
            inputs: {
              disabled : ( paymentMethod : PaymentMethodModel ) => !paymentMethod.editing || this.isCashType(paymentMethod),
              giveFocus: ( paymentMethod : PaymentMethodModel ) => paymentMethod.focusField === 'payment_method',
              model    : 'payment_method',
              name     : 'payment_method',
              required : true
            },
            outputs: {
              focus: ( paymentMethod : PaymentMethodModel ) => this.clearFocusField(paymentMethod, 'payment_method')
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        field : 'payment_method',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.PAYMENT_METHOD')
        }
      }
    ];

    if ( this.hasXeroAccountColumn() ) {
      columns.push({
        component: {
          bindings: {
            inputs: {
              disabled   : ( paymentMethod : PaymentMethodModel ) => !paymentMethod.editing || this.loadingAccounts,
              model      : 'account_code',
              name       : 'payment_method_account_code',
              options    : this.accounts,
              placeholder: this.loadingAccounts ? 'Loading...' : ''
            }
          },
          enabled: true,
          factory: TableSelectControlComponent
        },
        header: {
          text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.ACCOUNT')
        }
      });
    }

    columns.push(
      {
        component: {
          bindings: {
            inputs: {
              disabled: ( paymentMethod : PaymentMethodModel ) => !paymentMethod.editing,
              model   : 'use_eftpos',
              name    : 'payment_method_use_eftpos'
            }
          },
          enabled: true,
          factory: TableSwitchControlComponent
        },
        header: {
          text: this.companyService.country('usa')
            ? 'Integrated'
            : this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.USE_EFTPOS')
        }
      },
      {
        classes  : 'text-right',
        component: {
          bindings: {
            outputs: {
              onCancelClick: ( paymentMethod : PaymentMethodModel ) => this.toggleEditing(paymentMethod, false),
              onEditClick  : ( paymentMethod : PaymentMethodModel ) => this.toggleEditing(paymentMethod, true),
              onSaveClick  : ( paymentMethod : PaymentMethodModel ) => this.save(paymentMethod)
            }
          },
          enabled: true,
          factory: CompanyListsActionsComponent
        },
        sortable: false
      }
    );

    return {
      columns,
      empty: {
        text: this.translateService.instant('COMPANY_SPACE.LISTS.GRID.NO_PAYMENT_METHODS')
      },
      pagination: {
        enabled: false
      },
      rows: {
        highlight: {
          enabled: false
        }
      },
      sorting: {
        enabled: false
      }
    };
  }

  hasXeroAccountColumn () : boolean {
    return this.companyService.hasIntegration('xero')
      && this.companyService.isSubscriptionPlan('gold', false);
  }

  isCashType ( paymentMethod : PaymentMethodModel ) : boolean {
    return !!paymentMethod.payment_method
      && paymentMethod.payment_method.toLowerCase() === 'cash';
  }

  refreshTable () : void {
    this.paymentMethods = this.paymentMethods.map(paymentMethod => ({ ...paymentMethod }));
  }

  save ( paymentMethod : PaymentMethodModel ) : void {
    if ( !paymentMethod.payment_method ) {
      this.messagesService.error(this.translateService.instant('JS_SPACE.MESSAGES.INVALID_INPUT'));

      return;
    }

    this.companyListsService.savePaymentMethod(paymentMethod)
    .then(( paymentMethods : Array<PaymentMethodModel> ) => {
      this.paymentMethods = paymentMethods.map(method => ({
        ...method,
        editing   : false,
        focusField: undefined
      }));

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.PAYMENT_METHOD'), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  toggleEditing ( paymentMethod : PaymentMethodModel, editing : boolean ) : void {
    if ( editing ) {
      if ( paymentMethod.id && !this.originalMethods.has(paymentMethod.id) ) {
        this.originalMethods.set(paymentMethod.id, { ...paymentMethod, editing: false });
      }

      paymentMethod.editing    = true;
      paymentMethod.focusField = 'payment_method';
    }
    else if ( !paymentMethod.id ) {
      this.deletePaymentMethod(paymentMethod);

      return;
    }
    else {
      const originalMethod = this.originalMethods.get(paymentMethod.id);

      if ( originalMethod ) {
        Object.assign(paymentMethod, originalMethod);

        this.originalMethods.delete(paymentMethod.id);
      }

      paymentMethod.editing    = false;
      paymentMethod.focusField = undefined;
    }

    this.refreshTable();
  }
}
